import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  TextField,
  Paper,
  Divider,
  Alert,
  Chip,
  InputAdornment,
  CircularProgress,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Radio,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  PersonOutline,
  Lock,
  AppRegistration,
  Verified,
  ErrorOutline,
  ReceiptLong,
  LocalOffer,
  LocationOn,
  Payment,
  Add,
  Home,
  Business,
  CheckCircle,
} from '@mui/icons-material';
import { useCart } from '../../../context/CartContext';
import { useUser } from '../../../context/UserContext';
import { useNavigate } from 'react-router-dom';
import { apiList, invokeApi, invokeGetApi } from '../../../services/apiServices';
import { getProductImage } from '../../../utils/imageHelper';

// Dynamic Cashfree SDK loader
const loadCashfree = () => {
  return new Promise((resolve, reject) => {
    if (window.Cashfree) {
      return resolve(window.Cashfree);
    }
    const script = document.createElement('script');
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.async = true;
    script.onload = () => {
      if (window.Cashfree) {
        resolve(window.Cashfree);
      } else {
        reject(new Error('Cashfree SDK failed to initialize'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Cashfree script'));
    document.body.appendChild(script);
  });
};

// ── Country codes (comprehensive list with flag emoji support) ──
const COUNTRY_CODES = [
  { code: '+91', country: 'IN', name: 'India' },
  { code: '+1', country: 'US', name: 'United States' },
  { code: '+1', country: 'CA', name: 'Canada' },
  { code: '+44', country: 'GB', name: 'United Kingdom' },
  { code: '+61', country: 'AU', name: 'Australia' },
  { code: '+64', country: 'NZ', name: 'New Zealand' },
  { code: '+65', country: 'SG', name: 'Singapore' },
  { code: '+971', country: 'AE', name: 'UAE' },
  { code: '+966', country: 'SA', name: 'Saudi Arabia' },
  { code: '+974', country: 'QA', name: 'Qatar' },
  { code: '+973', country: 'BH', name: 'Bahrain' },
  { code: '+968', country: 'OM', name: 'Oman' },
  { code: '+965', country: 'KW', name: 'Kuwait' },
  { code: '+60', country: 'MY', name: 'Malaysia' },
  { code: '+62', country: 'ID', name: 'Indonesia' },
  { code: '+63', country: 'PH', name: 'Philippines' },
  { code: '+66', country: 'TH', name: 'Thailand' },
  { code: '+84', country: 'VN', name: 'Vietnam' },
  { code: '+880', country: 'BD', name: 'Bangladesh' },
  { code: '+94', country: 'LK', name: 'Sri Lanka' },
  { code: '+92', country: 'PK', name: 'Pakistan' },
  { code: '+977', country: 'NP', name: 'Nepal' },
  { code: '+960', country: 'MV', name: 'Maldives' },
  { code: '+975', country: 'BT', name: 'Bhutan' },
  { code: '+81', country: 'JP', name: 'Japan' },
  { code: '+82', country: 'KR', name: 'South Korea' },
  { code: '+86', country: 'CN', name: 'China' },
  { code: '+852', country: 'HK', name: 'Hong Kong' },
  { code: '+853', country: 'MO', name: 'Macau' },
  { code: '+886', country: 'TW', name: 'Taiwan' },
  { code: '+49', country: 'DE', name: 'Germany' },
  { code: '+33', country: 'FR', name: 'France' },
  { code: '+39', country: 'IT', name: 'Italy' },
  { code: '+34', country: 'ES', name: 'Spain' },
  { code: '+31', country: 'NL', name: 'Netherlands' },
  { code: '+32', country: 'BE', name: 'Belgium' },
  { code: '+41', country: 'CH', name: 'Switzerland' },
  { code: '+43', country: 'AT', name: 'Austria' },
  { code: '+46', country: 'SE', name: 'Sweden' },
  { code: '+47', country: 'NO', name: 'Norway' },
  { code: '+45', country: 'DK', name: 'Denmark' },
  { code: '+358', country: 'FI', name: 'Finland' },
  { code: '+353', country: 'IE', name: 'Ireland' },
  { code: '+351', country: 'PT', name: 'Portugal' },
  { code: '+30', country: 'GR', name: 'Greece' },
  { code: '+48', country: 'PL', name: 'Poland' },
  { code: '+7', country: 'RU', name: 'Russia' },
  { code: '+380', country: 'UA', name: 'Ukraine' },
  { code: '+27', country: 'ZA', name: 'South Africa' },
  { code: '+234', country: 'NG', name: 'Nigeria' },
  { code: '+20', country: 'EG', name: 'Egypt' },
  { code: '+254', country: 'KE', name: 'Kenya' },
  { code: '+233', country: 'GH', name: 'Ghana' },
  { code: '+55', country: 'BR', name: 'Brazil' },
  { code: '+52', country: 'MX', name: 'Mexico' },
  { code: '+54', country: 'AR', name: 'Argentina' },
  { code: '+56', country: 'CL', name: 'Chile' },
  { code: '+57', country: 'CO', name: 'Colombia' },
  { code: '+58', country: 'VE', name: 'Venezuela' },
  { code: '+51', country: 'PE', name: 'Peru' },
];

const FLAG_EMOJI = (countryCode) => {
  const offset = 127397;
  return [...countryCode.toUpperCase()].map(c => String.fromCodePoint(c.charCodeAt(0) + offset)).join('');
};

const parsePhone = (rawPhone) => {
  if (!rawPhone) return { code: '+91', number: '' };
  let str = String(rawPhone).trim();
  let matchedCode = '+91';
  for (const c of COUNTRY_CODES) {
    if (str.startsWith(c.code)) {
      matchedCode = c.code;
      str = str.slice(c.code.length).trim();
      break;
    }
  }
  const number = str.replace(/[^\d]/g, '');
  return { code: matchedCode, number };
};

// ── Coupon codes ──
const COUPONS = {
  'ARINFOTECH10': { type: 'percent', value: 10, label: '10% Off' },
  'WELCOME50': { type: 'flat', value: 50, label: '₹50 Off' },
  'BULK200': { type: 'flat', value: 200, label: '₹200 Off' },
};

const CheckoutPage = () => {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [selectedCountryIdx, setSelectedCountryIdx] = useState(0);
  const [addressForm, setAddressForm] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    companyName: '',
    gstNumber: '',
    addressLine1: '',
    addressLine2: '',
    pincode: '',
    city: '',
    state: '',
    country: ''
  });

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [isEnteringNewAddress, setIsEnteringNewAddress] = useState(false);
  const [saveAddressToAccount, setSaveAddressToAccount] = useState(true);

  const [errors, setErrors] = useState({});
  const [gstStatus, setGstStatus] = useState(null); // null | 'valid' | 'invalid'
  const [pincodeLoading, setPincodeLoading] = useState(false);

  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');

  const { cartItems: contextCartItems, clearCart } = useCart();
  const { user } = useUser();
  const navigate = useNavigate();

  // Auto-fill logged in user info (Name, Email, Phone) and load saved customer addresses
  useEffect(() => {
    if (!user) return;

    const storageKey = `ar_addresses_${user.id || user.email || 'guest'}`;
    let loadedAddresses = [];
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(stored)) {
        loadedAddresses = stored;
      }
    } catch (e) {}

    setSavedAddresses(loadedAddresses);

    const initialName = user.name || user.fullName || '';
    const initialEmail = user.email || '';
    const initialPhoneRaw = user.phone || user.phoneNumber || '';
    const parsed = parsePhone(initialPhoneRaw);

    if (parsed.code) {
      setCountryCode(parsed.code);
      const cIdx = COUNTRY_CODES.findIndex(c => c.code === parsed.code);
      if (cIdx !== -1) setSelectedCountryIdx(cIdx);
    }

    if (loadedAddresses.length > 0) {
      const defaultAddr = loadedAddresses.find(a => a.isDefault) || loadedAddresses[0];
      setSelectedAddressId(defaultAddr.id);
      setIsEnteringNewAddress(false);

      const addrPhoneParsed = parsePhone(defaultAddr.phone || initialPhoneRaw);

      setAddressForm(prev => ({
        ...prev,
        fullName: prev.fullName || defaultAddr.name || initialName,
        email: prev.email || initialEmail,
        phoneNumber: prev.phoneNumber || addrPhoneParsed.number || parsed.number,
        addressLine1: prev.addressLine1 || defaultAddr.address || '',
        addressLine2: prev.addressLine2 || defaultAddr.addressLine2 || '',
        city: prev.city || defaultAddr.city || '',
        state: prev.state || defaultAddr.state || '',
        pincode: prev.pincode || defaultAddr.pincode || '',
        country: prev.country || defaultAddr.country || 'India',
      }));
    } else {
      setSelectedAddressId('new');
      setIsEnteringNewAddress(true);
      setAddressForm(prev => ({
        ...prev,
        fullName: prev.fullName || initialName,
        email: prev.email || initialEmail,
        phoneNumber: prev.phoneNumber || parsed.number,
        addressLine1: prev.addressLine1 || user.address || '',
        city: prev.city || user.city || '',
        state: prev.state || user.state || '',
        pincode: prev.pincode || user.pincode || '',
        country: prev.country || user.country || 'India',
      }));
    }
  }, [user]);

  const handleSelectAddress = (addr) => {
    setSelectedAddressId(addr.id);
    setIsEnteringNewAddress(false);

    const phoneParsed = parsePhone(addr.phone || addressForm.phoneNumber);
    if (phoneParsed.code) {
      setCountryCode(phoneParsed.code);
      const cIdx = COUNTRY_CODES.findIndex(c => c.code === phoneParsed.code);
      if (cIdx !== -1) setSelectedCountryIdx(cIdx);
    }

    setAddressForm(prev => ({
      ...prev,
      fullName: addr.name || prev.fullName,
      phoneNumber: phoneParsed.number || prev.phoneNumber,
      addressLine1: addr.address || '',
      addressLine2: addr.addressLine2 || '',
      city: addr.city || '',
      state: addr.state || '',
      pincode: addr.pincode || '',
      country: addr.country || 'India',
    }));

    setErrors(prev => ({
      ...prev,
      fullName: '',
      phoneNumber: '',
      addressLine1: '',
      pincode: '',
      city: '',
      state: '',
      country: ''
    }));
  };

  const handleAddNewAddressClick = () => {
    setSelectedAddressId('new');
    setIsEnteringNewAddress(true);
    setAddressForm(prev => ({
      ...prev,
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
    }));
  };

  // Multi-source cart fallback: ensures cart is never lost if navigated from ProductGrid or direct link
  const [localCart, setLocalCart] = useState(() => {
    try {
      const sessionData = sessionStorage.getItem('cart');
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
      const localData = localStorage.getItem('arinfotech_cart') || localStorage.getItem('amrutha_cart');
      if (localData) {
        const parsed = JSON.parse(localData);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {
      console.error('Error loading fallback cart:', e);
    }
    return [];
  });

  const cartItems = (contextCartItems && contextCartItems.length > 0) ? contextCartItems : localCart;

  const subtotal = cartItems.reduce((acc, item) => {
    const price = parseFloat(item.price || item.unitPrice || 0);
    const qty = parseInt(item.quantity || item.qty || 1, 10);
    return acc + (price * qty);
  }, 0);

  // Discount calculation
  const discountAmount = appliedCoupon
    ? appliedCoupon.type === 'percent'
      ? (subtotal * appliedCoupon.value) / 100
      : Math.min(appliedCoupon.value, subtotal)
    : 0;

  const estimatedTotal = Math.max(0, subtotal - discountAmount);

  // ── Pincode auto-fill ──
  const handlePincodeChange = async (e) => {
    const pin = e.target.value;
    setAddressForm(prev => ({ ...prev, pincode: pin, city: '', state: '', country: '' }));

    if (pin.length === 6 && /^\d+$/.test(pin)) {
      setPincodeLoading(true);
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
        const data = await response.json();
        if (data && data[0].Status === 'Success') {
          const postOffice = data[0].PostOffice[0];
          setAddressForm(prev => ({
            ...prev,
            city: postOffice.District,
            state: postOffice.State,
            country: 'India'
          }));
        }
      } catch (err) {
        console.error('Pincode lookup failed:', err);
      } finally {
        setPincodeLoading(false);
      }
    }
  };

  const handleAddressChange = (field) => (e) => {
    setAddressForm(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // ── GST format on blur (No restrictive regex pattern) ──
  const handleGstBlur = () => {
    const gst = addressForm.gstNumber.trim().toUpperCase();
    if (!gst) {
      setGstStatus(null);
      return;
    }
    setGstStatus('provided');
    setAddressForm(prev => ({ ...prev, gstNumber: gst }));
  };

  // ── Form validation ──
  const validateForm = () => {
    const required = {
      fullName: 'Full name is required',
      phoneNumber: 'Phone number is required',
      email: 'Email address is required',
      addressLine1: 'Address is required',
      pincode: 'Pincode is required',
      city: 'City is required',
      state: 'State is required',
      country: 'Country is required',
    };

    const newErrors = {};

    Object.entries(required).forEach(([field, msg]) => {
      if (!addressForm[field]?.trim()) newErrors[field] = msg;
    });

    // Email format
    if (addressForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addressForm.email)) {
      newErrors.email = 'Enter a valid email address';
    }

    // Phone: digits only, 6–15 digits
    if (addressForm.phoneNumber && !/^\d{6,15}$/.test(addressForm.phoneNumber.replace(/\s/g, ''))) {
      newErrors.phoneNumber = 'Enter a valid phone number (digits only)';
    }

    // Cart validation
    if (!cartItems || cartItems.length === 0 || subtotal <= 0) {
      setPaymentError('Your cart is empty. Please add a product to your cart before proceeding to checkout.');
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Coupon apply ──
  const handleApplyCoupon = () => {
    setCouponError('');
    setCouponSuccess('');
    const code = couponInput.trim().toUpperCase();
    if (!code) { setCouponError('Please enter a coupon code.'); return; }
    const coupon = COUPONS[code];
    if (!coupon) {
      setCouponError('Invalid coupon code. Please check and try again.');
      return;
    }
    setAppliedCoupon({ ...coupon, code });
    setCouponSuccess(`Coupon "${code}" applied! You save ${coupon.type === 'percent' ? `${coupon.value}%` : `₹${coupon.value}`}.`);
    setCouponInput('');
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponError('');
    setCouponSuccess('');
  };

  // ── Cashfree direct payment ──
  const handleCashfreePayment = async () => {
    if (!validateForm()) return;

    setIsProcessingPayment(true);
    setPaymentError('');
    try {
      const CashfreeSDK = await loadCashfree();

      let mode = 'sandbox';
      try {
        const configRes = await invokeGetApi(apiList.getCashfreeConfig);
        if (configRes?.data?.mode) {
          mode = configRes.data.mode;
        }
      } catch (e) {
        console.log('Using default mode:', mode);
      }

      const cashfree = CashfreeSDK({ mode });

      const fullAddress = [
        addressForm.addressLine1,
        addressForm.addressLine2,
        addressForm.city,
        addressForm.state,
        addressForm.pincode,
        addressForm.country
      ].filter(Boolean).join(', ');

      const orderPayload = {
        totalAmount: estimatedTotal,
        fullName: addressForm.fullName,
        companyName: addressForm.companyName?.trim() || '',
        gstNumber: addressForm.gstNumber?.trim().toUpperCase() || '',
        email: addressForm.email,
        phoneNumber: `${countryCode}${addressForm.phoneNumber}`,
        address: fullAddress,
        items: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          size: item.size
        })),
        returnUrl: window.location.origin.replace(/^http:/, 'https:') + '/order-success'
      };

      const orderRes = await invokeApi(apiList.createCashfreeOrder, orderPayload);

      if (orderRes?.data?.payment_session_id) {
        // 1. Save new address to customer account if user is logged in
        if (user) {
          const storageKey = `ar_addresses_${user.id || user.email || 'guest'}`;
          try {
            const existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
            const newAddressText = [addressForm.addressLine1, addressForm.addressLine2].filter(Boolean).join(', ');
            const exists = existing.some(a => (a.address || '').toLowerCase() === newAddressText.toLowerCase() && String(a.pincode) === String(addressForm.pincode));
            if (!exists && (selectedAddressId === 'new' || existing.length === 0 || saveAddressToAccount)) {
              const newSaved = {
                id: `addr_${Date.now()}`,
                type: 'Home',
                name: addressForm.fullName,
                address: newAddressText,
                city: addressForm.city,
                state: addressForm.state,
                pincode: addressForm.pincode,
                country: addressForm.country || 'India',
                phone: `${countryCode} ${addressForm.phoneNumber}`,
                isDefault: existing.length === 0,
              };
              localStorage.setItem(storageKey, JSON.stringify([...existing, newSaved]));
            }
          } catch (e) {}
        }

        // 2. Save customer order into customer-specific orders storage for Order History
        try {
          const customerEmail = addressForm.email.toLowerCase().trim();
          const customerOrdersKey = `ar_customer_orders_${customerEmail}`;
          const existingCustOrders = JSON.parse(localStorage.getItem(customerOrdersKey) || '[]');
          const newCustOrder = {
            id: orderRes.data?.order_id || `ORD-${Date.now().toString().slice(-6)}`,
            order_number: orderRes.data?.order_id || `ORD-${Date.now().toString().slice(-6)}`,
            invoice_number: orderRes.data?.order_id || `ORD-${Date.now().toString().slice(-6)}`,
            customer_name: addressForm.fullName,
            customer_email: addressForm.email,
            customer_phone: `${countryCode} ${addressForm.phoneNumber}`,
            company_name: addressForm.companyName || '',
            gst_number: addressForm.gstNumber || '',
            shipping_address: fullAddress,
            address: fullAddress,
            total_amount: estimatedTotal,
            total: estimatedTotal,
            items: cartItems.map(item => ({
              description: item.name,
              name: item.name,
              quantity: item.quantity,
              unit_price: item.price,
              price: item.price,
              total_price: item.quantity * item.price,
              hsn_sac: '997331'
            })),
            order_status: 'Processing',
            payment_status: 'Paid',
            created_at: new Date().toISOString()
          };
          localStorage.setItem(customerOrdersKey, JSON.stringify([newCustOrder, ...existingCustOrders]));

          // Sync immediately to ar_dashboard_orders for Dashboard Orders Manager
          try {
            const dashOrders = JSON.parse(localStorage.getItem('ar_dashboard_orders') || '[]');
            const otherOrders = dashOrders.filter(o => String(o.order_number || o.id) !== String(newCustOrder.order_number));
            localStorage.setItem('ar_dashboard_orders', JSON.stringify([newCustOrder, ...otherOrders]));
            try {
              window.dispatchEvent(new Event('storage'));
              window.dispatchEvent(new CustomEvent('ar_orders_updated'));
            } catch (e) {}
          } catch (e) {}
        } catch (e) {}

        clearCart();
        cashfree.checkout({
          paymentSessionId: orderRes.data.payment_session_id,
          redirectTarget: '_self'
        });
      } else {
        throw new Error(orderRes?.data?.responseMessage || 'Could not initiate Cashfree payment session');
      }
    } catch (err) {
      console.error('Cashfree Checkout Error:', err);
      setPaymentError(err.message || 'Payment initiation failed. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  // Show review section as soon as user starts filling
  const isAddressPartiallyFilled = !!(addressForm.fullName || addressForm.addressLine1 || addressForm.city);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, sm: 3 } }}>
      <Typography variant="h3" align="center" gutterBottom mb={{ xs: 3, md: 6 }} sx={{ fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' } }}>
        Checkout
      </Typography>

      <Grid container spacing={4}>
        {/* ── Left Column ── */}
        <Grid item xs={12} md={8}>

          {/* ── Guest Sign-In Banner ── */}
          {!user && (
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 3,
                overflow: 'hidden',
                border: '1.5px solid #e3edff',
                background: 'linear-gradient(135deg, #f0f6ff 0%, #ffffff 60%, #f5f0ff 100%)'
              }}
            >
              <Box sx={{ px: 3, pt: 3, pb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a237e', mb: 0.5 }}>
                  Have an account? Get faster checkout!
                </Typography>
                <Typography variant="body2" sx={{ color: '#555', mb: 2.5 }}>
                  Sign in to use your saved addresses, track orders, and earn rewards. Or continue below as a guest.
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, px: 3, py: 2.5 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PersonOutline />}
                  onClick={() => navigate('/login')}
                  sx={{
                    flex: 1, py: 1.5, borderRadius: 2.5, fontWeight: 700, fontSize: '1rem',
                    background: 'linear-gradient(135deg, #1565c0, #0e6ada)',
                    boxShadow: '0 4px 15px rgba(14,106,218,0.35)',
                    textTransform: 'none',
                    '&:hover': { background: 'linear-gradient(135deg, #0d47a1, #0958b8)' }
                  }}
                >
                  Sign In
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<AppRegistration />}
                  onClick={() => navigate('/login')}
                  sx={{
                    flex: 1, py: 1.5, borderRadius: 2.5, fontWeight: 700, fontSize: '1rem',
                    borderColor: '#1565c0', color: '#1565c0', borderWidth: 1.5, textTransform: 'none',
                    '&:hover': { borderColor: '#0d47a1', bgcolor: '#f0f6ff', borderWidth: 1.5 }
                  }}
                >
                  Create Account
                </Button>
              </Box>
              <Box sx={{ px: 3, pb: 2 }}>
                <Typography variant="caption" sx={{ color: '#888', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Lock sx={{ fontSize: 13 }} /> Your data is safe & encrypted. You can also fill the form below as a guest.
                </Typography>
              </Box>
            </Paper>
          )}

          {/* ── Main Form Paper ── */}
          <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {user ? 'Shipping Address' : 'Guest Shipping Address'}
              </Typography>
              {user && (
                <Chip
                  icon={<Verified sx={{ fontSize: 16 }} />}
                  label="Logged In Customer"
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 700, borderRadius: 1.5 }}
                />
              )}
            </Box>

            {/* ── Saved Addresses Selector (Professional E-Commerce Style) ── */}
            {user && savedAddresses.length > 0 && (
              <Box sx={{ mb: 3.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn sx={{ color: '#0E6ADA', fontSize: 22 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B', letterSpacing: 0.5 }}>
                      SELECT A DELIVERY ADDRESS ({savedAddresses.length})
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add sx={{ fontSize: 18 }} />}
                    onClick={handleAddNewAddressClick}
                    sx={{
                      fontWeight: 700,
                      borderRadius: 2,
                      textTransform: 'none',
                      borderColor: selectedAddressId === 'new' ? '#0E6ADA' : '#CBD5E1',
                      bgcolor: selectedAddressId === 'new' ? '#EFF6FF' : 'transparent',
                      color: selectedAddressId === 'new' ? '#0E6ADA' : '#475569',
                      '&:hover': { bgcolor: '#EFF6FF', borderColor: '#0E6ADA' }
                    }}
                  >
                    + Enter New Address
                  </Button>
                </Box>

                <Grid container spacing={2}>
                  {savedAddresses.map((addr) => {
                    const isSelected = selectedAddressId === addr.id;
                    return (
                      <Grid item xs={12} sm={6} key={addr.id}>
                        <Paper
                          elevation={0}
                          onClick={() => handleSelectAddress(addr)}
                          sx={{
                            p: 2,
                            borderRadius: 2.5,
                            cursor: 'pointer',
                            border: isSelected ? '2px solid #0E6ADA' : '1.5px solid #E2E8F0',
                            bgcolor: isSelected ? '#F0F6FF' : '#FFFFFF',
                            boxShadow: isSelected ? '0 4px 14px rgba(14, 106, 218, 0.12)' : 'none',
                            position: 'relative',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: isSelected ? '#0E6ADA' : '#94A3B8',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.06)'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Radio
                                checked={isSelected}
                                onChange={() => handleSelectAddress(addr)}
                                value={addr.id}
                                size="small"
                                sx={{ p: 0, color: isSelected ? '#0E6ADA' : '#94A3B8' }}
                              />
                              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1E293B' }}>
                                {addr.name || 'Account Holder'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Chip
                                label={addr.type || 'Home'}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  bgcolor: addr.type === 'Work' ? '#FEF3C7' : '#E0E7FF',
                                  color: addr.type === 'Work' ? '#92400E' : '#3730A3'
                                }}
                              />
                              {addr.isDefault && (
                                <Chip
                                  label="DEFAULT"
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: '#DCFCE7', color: '#166534' }}
                                />
                              )}
                            </Box>
                          </Box>

                          <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem', lineHeight: 1.5, pl: 3.2 }}>
                            {addr.address}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem', pl: 3.2 }}>
                            {[addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')}
                          </Typography>
                          {addr.phone && (
                            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.8, pl: 3.2 }}>
                              Phone: <strong>{addr.phone}</strong>
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>

                <Alert severity="info" sx={{ mt: 2, borderRadius: 2, py: 0.5, bgcolor: '#F0F7FF', color: '#0E6ADA', border: '1px solid #BFDBFE' }}>
                  {selectedAddressId === 'new'
                    ? 'Entering new delivery details below. Fill the form to proceed.'
                    : '✓ Selected address details are automatically loaded below. All fields remain fully editable.'}
                </Alert>

                <Divider sx={{ my: 3 }} />
              </Box>
            )}

            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              {user && savedAddresses.length > 0 && selectedAddressId !== 'new' ? 'Edit / Confirm Details' : 'Contact & Address Information'}
            </Typography>

            <Grid container spacing={2.5}>

              <Grid item xs={12}>
                <TextField
                  fullWidth label="Full Name *" variant="outlined"
                  value={addressForm.fullName} onChange={handleAddressChange('fullName')}
                  error={!!errors.fullName} helperText={errors.fullName}
                />
              </Grid>

              {/* ── Phone Number with Country Code Dropdown ── */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <FormControl sx={{ minWidth: 120 }} error={!!errors.phoneNumber}>
                    <Select
                      value={selectedCountryIdx}
                      onChange={(e) => {
                        const idx = e.target.value;
                        setSelectedCountryIdx(idx);
                        setCountryCode(COUNTRY_CODES[idx].code);
                      }}
                      variant="outlined"
                      sx={{
                        height: '56px',
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        '.MuiSelect-select': { py: '14px', px: '10px' }
                      }}
                      MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                      renderValue={(idx) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span style={{ fontSize: '1.1rem' }}>{FLAG_EMOJI(COUNTRY_CODES[idx].country)}</span>
                          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{COUNTRY_CODES[idx].code}</span>
                        </Box>
                      )}
                    >
                      {COUNTRY_CODES.map((c, idx) => (
                        <MenuItem key={`${c.country}-${idx}`} value={idx} sx={{ fontSize: '0.875rem', gap: 1 }}>
                          <span style={{ fontSize: '1.1rem', marginRight: 6 }}>{FLAG_EMOJI(c.country)}</span>
                          <span style={{ fontWeight: 600, marginRight: 6, minWidth: 42 }}>{c.code}</span>
                          <span style={{ color: '#555' }}>{c.name}</span>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Phone Number *"
                    variant="outlined"
                    value={addressForm.phoneNumber}
                    onChange={handleAddressChange('phoneNumber')}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber || 'e.g. 9876543210'}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Email Address *" variant="outlined" type="email"
                  value={addressForm.email} onChange={handleAddressChange('email')}
                  error={!!errors.email} helperText={errors.email}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Company Name (Optional)" variant="outlined"
                  value={addressForm.companyName} onChange={handleAddressChange('companyName')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="GST Number (Optional)"
                  variant="outlined"
                  value={addressForm.gstNumber}
                  onChange={handleAddressChange('gstNumber')}
                  onBlur={handleGstBlur}
                  placeholder="Enter GSTIN if available"
                  InputProps={{
                    endAdornment: addressForm.gstNumber?.trim() ? (
                      <InputAdornment position="end">
                        <Verified sx={{ color: 'success.main' }} />
                      </InputAdornment>
                    ) : null
                  }}
                  helperText={
                    addressForm.gstNumber?.trim()
                      ? '✓ GSTIN will be included on your B2B tax invoice'
                      : 'Optional — Provide GSTIN to claim Input Tax Credit (ITC)'
                  }
                />
              </Grid>

              <Collapse in={Boolean(addressForm.gstNumber?.trim())} sx={{ width: '100%', px: 1.25, mt: -1 }}>
                <Alert severity="success" icon={<ReceiptLong fontSize="small" />} sx={{ borderRadius: 2, py: 0.5 }}>
                  <strong>B2B GST Invoice</strong>: GST @ 18% is applied. You can claim Input Tax Credit (ITC) using your GSTIN.
                </Alert>
              </Collapse>

              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Delivery Address</Typography>
                </Divider>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth label="Address Line 1 — House/flat number, building, street *"
                  variant="outlined" value={addressForm.addressLine1} onChange={handleAddressChange('addressLine1')}
                  error={!!errors.addressLine1} helperText={errors.addressLine1}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth label="Address Line 2 — Apartment, area, landmark (optional)"
                  variant="outlined" value={addressForm.addressLine2} onChange={handleAddressChange('addressLine2')}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth label="Pin Code / ZIP Code *" variant="outlined"
                  value={addressForm.pincode} onChange={handlePincodeChange}
                  error={!!errors.pincode} helperText={errors.pincode}
                  InputProps={{
                    endAdornment: pincodeLoading && (
                      <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth label="City *" variant="outlined"
                  value={addressForm.city} onChange={handleAddressChange('city')}
                  error={!!errors.city} helperText={errors.city}
                  InputLabelProps={{ shrink: !!addressForm.city }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth label="State *" variant="outlined"
                  value={addressForm.state} onChange={handleAddressChange('state')}
                  error={!!errors.state} helperText={errors.state}
                  InputLabelProps={{ shrink: !!addressForm.state }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth label="Country *" variant="outlined"
                  value={addressForm.country} onChange={handleAddressChange('country')}
                  error={!!errors.country} helperText={errors.country}
                  InputLabelProps={{ shrink: !!addressForm.country }}
                />
              </Grid>

              {user && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={saveAddressToAccount}
                        onChange={(e) => setSaveAddressToAccount(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
                        Save this address to my account for faster checkout next time
                      </Typography>
                    }
                  />
                </Grid>
              )}
            </Grid>

            {/* ── Dynamic: Review Your Order (inline, below Delivery Address) ── */}
            <Collapse in={isAddressPartiallyFilled}>
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3 }}>
                  <Chip
                    icon={<LocationOn sx={{ fontSize: 16 }} />}
                    label="Review Your Order"
                    sx={{ fontWeight: 700, fontSize: '0.82rem', px: 1 }}
                    color="primary"
                    variant="outlined"
                  />
                </Divider>

                {/* Delivery Summary Card */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    mb: 3,
                    bgcolor: '#F8FAFF',
                    border: '1.5px solid #dce9ff'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary', letterSpacing: 1, fontSize: '0.72rem' }}>
                    DELIVERING TO
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <LocationOn sx={{ color: '#0e6ada', mt: 0.2, fontSize: 20 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
                        {addressForm.fullName || <span style={{ color: '#aaa', fontStyle: 'italic' }}>Full Name</span>}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, lineHeight: 1.6 }}>
                        {[addressForm.addressLine1, addressForm.addressLine2, addressForm.city, addressForm.state, addressForm.pincode, addressForm.country]
                          .filter(Boolean).join(', ') || <span style={{ color: '#bbb', fontStyle: 'italic' }}>Address will appear here</span>}
                      </Typography>
                      {(addressForm.email || addressForm.phoneNumber) && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                          {addressForm.email}
                          {addressForm.email && addressForm.phoneNumber && ' · '}
                          {addressForm.phoneNumber && `${countryCode} ${addressForm.phoneNumber}`}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>

                {/* Order Items */}
                {cartItems.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary', letterSpacing: 1, fontSize: '0.72rem' }}>
                      ORDER ITEMS
                    </Typography>
                    {cartItems.map((item) => (
                      <Box
                        key={`${item.id}-${item.size}`}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1.5,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: '#F8FAFC',
                          border: '1px solid #E2E8F0'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            component="img"
                            src={getProductImage(item.image || item.featuredImage)}
                            alt={item.name}
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: '#FFF',
                              borderRadius: 1,
                              border: '1px solid #E2E8F0',
                              objectFit: 'contain',
                              p: 0.4
                            }}
                          />
                          <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#0F172A' }}>{item.name}</Typography>
                            {item.size && (
                              <Typography variant="caption" color="text.secondary">License: {item.size}</Typography>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ fontWeight: 800, color: '#0e6ada', fontSize: '0.92rem' }}>₹{(item.price * item.quantity).toLocaleString('en-IN')}</Typography>
                          <Typography variant="caption" color="text.secondary">Qty: {item.quantity}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {gstStatus === 'valid' && (
                  <Alert severity="info" icon={<ReceiptLong fontSize="small" />} sx={{ mb: 2, borderRadius: 2 }}>
                    GST Invoice will be sent to <strong>{addressForm.email}</strong> for GSTIN: <strong>{addressForm.gstNumber}</strong>
                  </Alert>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  By placing this order, you agree to our{' '}
                  <a href="/terms-conditions" style={{ color: '#0e6ada' }}>terms and conditions</a>.
                </Typography>
              </Box>
            </Collapse>

            {/* ── Payment Error ── */}
            {paymentError && (
              <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
                {paymentError}
              </Alert>
            )}

            {/* ── Cashfree Payment Button ── */}
            <Box sx={{ mt: 4 }}>
              <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2, bgcolor: '#ECFDF5', color: '#065F46' }}>
                🔒 <strong>100% Safe &amp; Secure Checkout</strong> — 256-bit encrypted bank-grade checkout powered by Cashfree Payments.
              </Alert>

              <Button
                onClick={handleCashfreePayment}
                variant="contained"
                size="large"
                fullWidth
                disabled={isProcessingPayment}
                startIcon={isProcessingPayment ? <CircularProgress size={20} color="inherit" /> : <Payment />}
                sx={{
                  py: 2,
                  borderRadius: 2.5,
                  fontWeight: 800,
                  fontSize: '1.05rem',
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #1565c0, #0e6ada)',
                  boxShadow: '0 6px 20px rgba(14,106,218,0.4)',
                  letterSpacing: 0.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0d47a1, #0958b8)',
                    boxShadow: '0 8px 25px rgba(14,106,218,0.5)',
                    transform: 'translateY(-1px)'
                  },
                  '&:active': { transform: 'translateY(0)' },
                  transition: 'all 0.2s ease'
                }}
              >
                {isProcessingPayment ? 'Initiating Payment…' : '🔐 Pay via Cashfree Payment Gateway'}
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">Accepts:</Typography>
                {['UPI', 'Credit Card', 'Debit Card', 'Net Banking', 'Wallets'].map(method => (
                  <Chip key={method} label={method} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20, fontWeight: 600 }} />
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* ── Right Column: Cart Summary ── */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 4, borderRadius: 3, backgroundColor: '#F3F7FF', border: '1px solid #dce9ff', position: 'sticky', top: 90 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 800 }}>Order Summary</Typography>

            {cartItems.map((item) => (
              <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '70%' }}>
                  {item.name} × {item.quantity}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{item.price * item.quantity}</Typography>
              </Box>
            ))}

            <Divider sx={{ my: 2 }} />

            {/* ── Coupon Section ── */}
            {/* <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocalOffer sx={{ fontSize: 16 }} /> Coupon Code
              </Typography>

              {appliedCoupon ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    icon={<CheckCircle sx={{ fontSize: 16 }} />}
                    label={`${appliedCoupon.code} — ${appliedCoupon.label}`}
                    color="success"
                    onDelete={handleRemoveCoupon}
                    sx={{ fontWeight: 700, flexGrow: 1 }}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Enter coupon code"
                    value={couponInput}
                    onChange={e => { setCouponInput(e.target.value); setCouponError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                    InputProps={{ sx: { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.85rem' } }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleApplyCoupon}
                    sx={{ borderRadius: 1.5, fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap', px: 2 }}
                  >
                    Apply
                  </Button>
                </Box>
              )}

              {couponError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{couponError}</Typography>
              )}
              {couponSuccess && (
                <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
                  ✓ {couponSuccess}
                </Typography>
              )}
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                Try: ARINFOTECH10 · WELCOME50 · BULK200
              </Typography>
            </Box> */}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography color="text.secondary">Items Subtotal</Typography>
              <Typography sx={{ fontWeight: 700 }}>₹{subtotal}</Typography>
            </Box>

            {appliedCoupon && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography color="success.main" sx={{ fontWeight: 600 }}>Discount ({appliedCoupon.code})</Typography>
                <Typography sx={{ fontWeight: 700, color: 'success.main' }}>−₹{discountAmount.toFixed(2)}</Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Total</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0e6ada' }}>
                ₹{estimatedTotal.toFixed(2)}
              </Typography>
            </Box>

            {/* {shipping > 0 && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2, py: 0.5, fontSize: '0.75rem' }}>
                Add ₹{999 - subtotal} more to get <strong>FREE shipping</strong>!
              </Alert>
            )} */}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CheckoutPage;
